
import React, { useEffect, useState } from 'react';
import useTitle from '../hooks/useTitle';
import {
  FaGlobeAmericas,
  FaServer,
  FaCodeBranch,
  FaMicrochip,
  FaArrowUp,
  FaArrowDown,
  FaFlag,
  FaLayerGroup,
} from 'react-icons/fa';
import { fetchPeerInfo, fetchGeolocateIP } from '../services/apiService';

const PeerInfoPage = () => {
  useTitle("MENELIK - Peer Info");
  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;


  const parseIpFromAddress = (addr) => addr.split(':')[0];

  useEffect(() => {
    const loadPeerData = async () => {
      try {
        const peerInfo = await fetchPeerInfo();
        // Geolocaliza cada IP en paralelo
        const enrichedPeers = await Promise.all(
          peerInfo.map(async (peer) => {
            const ip = parseIpFromAddress(peer.address);
            const { countryCode, countryName } = await fetchGeolocateIP(ip);
            return {
              ...peer,
              ip,
              countryCode,
              countryName,
            };
          })
        );
        setPeers(enrichedPeers);
      } catch (err) {
        console.error('Error loading peer data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPeerData();
  }, []);

  // PAGINACIÓN
  const totalItems = peers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPeers = peers.slice(startIndex, endIndex);

  const goToPreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  // VERSIONES SUBVER
  const versionCounts = peers.reduce((acc, peer) => {
    const version = peer.subver || 'UnknownVersion';
    if (!acc[version]) {
      acc[version] = 0;
    }
    acc[version]++;
    return acc;
  }, {});

  const versionEntries = Object.entries(versionCounts);

  if (loading) {
    return <p className="text-center text-lg text-gray-500 my-8">Loading peer info...</p>;
  }

  if (!peers || peers.length === 0) {
    return <p className="text-center text-lg text-gray-500 my-8">No peer info available.</p>;
  }

  return (
    <div className="container mx-auto p-4">

      <h1 className="text-3xl font-bold mb-4 flex items-center text-gray-800">
        <FaServer className="mr-2 text-blue-600" />
        Peer Info
      </h1>

      <div className="bg-white shadow-lg rounded-lg p-4 border border-gray-200 mb-6">
        <h2 className="text-xl font-semibold mb-2 flex items-center text-gray-800">
          <FaLayerGroup className="mr-2 text-green-600" />
          Versions Overview
        </h2>

        <div className="flex flex-wrap gap-4 mt-2">
          {versionEntries.map(([version, count]) => (
            <div
              key={version}
              className="bg-gray-50 border border-gray-100 p-2 rounded shadow-sm flex flex-col items-center"
            >
              <span className="text-sm text-gray-500">Version</span>
              <strong className="text-gray-700 text-center break-all max-w-[200px]">
                {version}
              </strong>
              <span className="text-sm text-gray-500 mt-1">Peers: {count}</span>
            </div>
          ))}
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <strong>Total Peers: </strong> {peers.length}
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg p-4 border border-gray-200 overflow-x-auto">
        <h2 className="text-2xl font-semibold mb-4 flex items-center text-gray-800">
          <FaMicrochip className="mr-2 text-green-600" /> Connected Peers
        </h2>

        <table className="table-auto w-full min-w-[600px] bg-gray-50 rounded">
          <thead>
            <tr className="bg-gray-200 text-gray-700">
              <th className="p-3 border-b">
                <div className="flex items-center">
                  <FaServer className="mr-1 text-gray-500" />
                  IP
                </div>
              </th>
              <th className="p-3 border-b">
                <div className="flex items-center">
                  <FaCodeBranch className="mr-1 text-gray-500" />
                  Version
                </div>
              </th>
              <th className="p-3 border-b">
                <div className="flex items-center">
                  <FaMicrochip className="mr-1 text-gray-500" />
                  Protocol
                </div>
              </th>
              <th className="p-3 border-b">
                <div className="flex items-center">
                  <FaArrowUp className="mr-1 text-gray-500" />
                  Last Send
                </div>
              </th>
              <th className="p-3 border-b">
                <div className="flex items-center">
                  <FaArrowDown className="mr-1 text-gray-500" />
                  Last Receive
                </div>
              </th>
              <th className="p-3 border-b">
                <div className="flex items-center">
                  <FaFlag className="mr-1 text-gray-500" />
                  Country
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentPeers.map((peer, idx) => (
              <tr key={idx} className="hover:bg-gray-100">
                <td className="p-3 border-b break-all">{peer.address}</td>
                <td className="p-3 border-b text-sm">{peer.subver}</td>
                <td className="p-3 border-b">{peer.protocol}</td>
                <td className="p-3 border-b text-sm">
                  {new Date(peer.lastSend * 1000).toLocaleString()}
                </td>
                <td className="p-3 border-b text-sm">
                  {new Date(peer.lastReceive * 1000).toLocaleString()}
                </td>
                <td className="p-3 border-b text-center">
                  {peer.countryCode ? (
                    <div className="flex flex-col items-center">
                      <img
                        src={`https://flagcdn.com/16x12/${peer.countryCode.toLowerCase()}.png`}
                        alt={peer.countryName || 'Flag'}
                        className="inline-block mb-1"
                      />
                      <span className="text-sm text-gray-700">{peer.countryName}</span>
                    </div>
                  ) : (
                    <FaGlobeAmericas className="inline-block text-gray-500 text-lg" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Paginador */}
        <div className="flex justify-between items-center mt-4">
          <button
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <p className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </p>
          <button
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PeerInfoPage;
